import { createRoot } from 'react-dom/client';
import React, { useState, useEffect } from 'react';
import { BrowserRouter, Route } from 'react-router-dom';

import io from 'socket.io-client';
import Helmet from 'react-helmet';
import classnames from 'classnames';

import { useSingleEffect, useLocalStorage, useMediaQuery } from 'react-haiku';

// Import all app's styles
import './index.css';
import './scss/mathquill.css';
import './scss/index.scss';

// Import app's context
import Context from 'contexts/App';

// App's login and forgot components
import Authentication from 'containers/Authentication';

// App's service containers
import Preloader from 'containers/System/Preloader';

// Mandatory components
import Navigation from 'components/Misc/Navigation';
import Header from 'components/Misc/Header';
import Notifications from 'components/Misc/Notifications';
import Messenger from 'components/Messenger';
import Footer from 'components/Footer';

// Routes based on user's role
import Administrator from 'containers/Administrator';
import Teacher from 'containers/Teacher';
import Student from 'containers/Student';
import Parent from 'containers/Parent';

import Show from 'components/Show';

import { useAppStore } from 'store/useAppStore';
// import { createCertificate } from 'utils/Document';

const App = () => {
	// Use app's store
	const [authenticated, setAuthenticated] = useAppStore(state => [state.authenticated, state.setAuthenticated]);
	const [user, setUser] = useAppStore(state => [state.user, state.setUser]);
	const [socket, setSocket] = useAppStore(state => [state.socket, state.setSocket]);
	const [navigation, setNavigation] = useAppStore(state => [state.navigation, state.setNavigation]);
	const [theme, setTheme] = useAppStore(state => [state.theme, state.setTheme]);
	const [title, setTitle] = useAppStore(state => [state.title, state.setTitle]);
	const [valid] = useAppStore(state => [state.valid]);
	const [onReset] = useAppStore(state => [state.onReset]);

	// Use local storage
	const [storageToken] = useLocalStorage('token');
	const [storageUser] = useLocalStorage('user');
	const [storageTheme] = useLocalStorage('theme');

	// Use prefers theme
	const prefersDark = useMediaQuery('(prefers-color-scheme: dark)');

	// Check if viewing from mobile
	const isMobile = useMediaQuery('(max-width: 768px)');

	useSingleEffect(() => {
		// Update authentication and users data state
		if (storageToken && storageUser) {
			setAuthenticated(true);
			setUser(storageUser);
		}
		
		// Check which theme user prefers
		if (storageTheme === 'dark' || (!storageTheme && prefersDark)) {
			setTheme('dark');
		} else {
			setTheme('light');
		}
	});

	// Update app's theme based on user's preference
	useEffect(() => {
		if (storageTheme) setTheme(storageTheme);
	}, [storageTheme]);

	// Handle socket connection
	useEffect(() => {
		// Do not proceed if socket not established
		if (valid && !socket) {
			// eslint-disable-next-line
			return setSocket(io(process.env.REACT_APP_SOCKET, {
				path: '/socket/',
				reconnection: true,
				reconnectionDelay: 500,
				reconnectionAttempts: Infinity
			}));
		}

		// Listen on socket connection
		socket?.on('connect', () => {
			// Emit handshake signal on socket's connection
			socket.emit('handshake', {
				id: user.id
			});

			// Debug log
			// eslint-disable-next-line
			if (process.env.NODE_ENV === 'development') {
				console.log('Socket:Connected:', socket.id);
			}
		});

		return () => {
			// Close socket connection on unmount
			socket?.close();

			// Debug log
			// eslint-disable-next-line
			if (process.env.NODE_ENV === 'development') {
				console.log('Socket:DisconnectedUnmount');
			}
		};
	}, [valid, socket]);

	// Reset app's store on unmount
	useEffect(() => {
		return () => {
			onReset();
		};
	}, []);

	// Cache active section
	const [section, setSection] = useState('');

	// Combine className for application's wrapper
	const wrapClass = classnames(
		'pt-14 flex flex-col flex-1 transition-all',
		!isMobile && 'ml-[260px]',
		(isMobile && navigation) && 'blur-sm'
	);
	
	// useSingleEffect(() => {
	// createCertificate();
	// });

	// Render authentication page
	if (!authenticated) return (
		<BrowserRouter>
			<Route path="*">
				<Authentication />
			</Route>
		</BrowserRouter>
	);

	return (
		<Context.Provider value={{
			navigation: navigation,
			setNavigation: setNavigation,
			section: section,
			setSection: setSection,
			user: user,
			title: title,
			setTitle: setTitle,
			socket: socket
		}}
		>
			<BrowserRouter>
				{/* App's container */}
				<>
					{/* App's title handler */}
					<Helmet>
						<title>{title}</title>
						<html lang="lv" className={theme === 'dark' ? 'dark' : ''} />
						<body className="bg-neutral-50 dark:bg-neutral-900 transition-colors antialiased" />
					</Helmet>

					{/* Show preloader if needed */}
					<Preloader visible={!valid} />

					{/* Header's component */}
					<Header />

					{/* Navigation's component */}
					<Navigation />

					{/* Messenger's component */}
					<Messenger />

					{/* App's main content */}
					<div className={wrapClass}>
						{/* Administrator's routes */}
						<Show if={user.role === 'admin' || user.role === 'employee'}>
							<Administrator role={user.role} />
						</Show>

						{/* Teacher's routes */}
						<Show if={user.role === 'teacher'}>
							<Teacher />
						</Show>

						{/* Student's routes */}
						<Show if={user.role === 'student'}>
							<Student />
						</Show>

						{/* Parent's routes */}
						<Show if={user.role === 'parent'}>
							<Parent />
						</Show>

						{/* Footer's component */}
						<Footer />
					</div>

					{/* Notification's component */}
					<Notifications />
				</>
			</BrowserRouter>
		</Context.Provider>
	);
};

// React's new implementation
createRoot(document.getElementById('app')).render(<App />);
